class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.compareSubtotalNode = this.drawer.querySelector('[data-cart-drawer-compare-subtotal]');
    this.currency = this.drawer.dataset.cartCurrency || 'USD';
    this.showCompareAt = this.drawer.dataset.cartDrawerShowCompareAt === 'true';
    this.upgradeConfig = this.parseJsonNode('[data-cart-drawer-upgrade-config]');
    this.variantCompareAtPrices = this.parseVariantCompareAtPrices();
    this.countdownNode = this.drawer.querySelector('[data-cart-drawer-countdown]');
    this.countdownTimeNode = this.drawer.querySelector('[data-cart-drawer-countdown-time]');
    this.countdownStorageKey = 'cart-drawer-countdown-ends-at';
    this.countdownInterval = null;
    this.activeAddButton = null;
    this.activeAddLabel = null;
    this.activeAddLabelText = '';
    this.lastActiveElement = null;
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;
    this.closeLabel = this.drawer.dataset.cartDrawerCloseLabel || 'Close cart';
    this.emptyTitle = this.drawer.dataset.cartDrawerEmptyTitle || 'Your cart is empty';
    this.emptyCopy = this.drawer.dataset.cartDrawerEmptyCopy || 'Add something you love and it will appear here.';
    this.decreaseQuantityLabel = this.drawer.dataset.cartDrawerDecreaseLabel || 'Decrease quantity';
    this.increaseQuantityLabel = this.drawer.dataset.cartDrawerIncreaseLabel || 'Increase quantity';
    this.removeLabel = this.drawer.dataset.cartDrawerRemoveLabel || 'Remove';
    this.subtotalLabel = this.drawer.dataset.cartDrawerSubtotalLabel || 'Subtotal';
    this.secureCheckoutLabel = this.drawer.dataset.cartDrawerSecureCheckoutLabel || 'Secure checkout';
    this.upgradeCompleteLabel =
      this.drawer.dataset.cartDrawerUpgradeCompleteLabel || 'You unlocked all available cart upgrades.';
    this.upgradeRemainingTemplate =
      this.drawer.dataset.cartDrawerUpgradeRemainingTemplate || '[[amount]] to [[reward]]';
    this.liveRegionTemplate =
      this.drawer.dataset.cartDrawerLiveTemplate || 'Cart updated. [[count]] items. Subtotal [[subtotal]].';
    this.addingLabel = this.drawer.dataset.cartDrawerAddingLabel || 'Adding...';

    this.bindUI();
  }

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        this.lastActiveElement = trigger;
        await this.refresh();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      } else if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        this.trapFocus(event);
      }
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
  }

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();

    const formData = new FormData(form);
    this.activeAddButton = submitter;
    this.activeAddLabel = submitter.querySelector('[data-add-to-cart-label]');
    this.activeAddLabelText = this.activeAddLabel?.textContent || '';
    this.cacheFormVariantCompareAt(form);

    try {
      this.setAddButtonState(true);

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Unable to add item to cart');
      }

      await response.json();
      await this.refresh();
      this.open();
    } catch (error) {
      console.error(error);
      window.location.href = form.action || '/cart';
    } finally {
      this.setAddButtonState(false);
    }
  }

  async handleDrawerClick(event) {
    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
    if (quantityTrigger) {
      const item = quantityTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      const delta = Number(quantityTrigger.dataset.cartQuantityDelta || 0);
      const quantityValue = item.querySelector('.cart-drawer__quantity-value');
      const currentQuantity = Number(quantityValue?.textContent || 0);
      const nextQuantity = Math.max(0, currentQuantity + delta);

      await this.changeItem(key, nextQuantity);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');
    if (removeTrigger) {
      const item = removeTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      await this.changeItem(key, 0);
    }
  }

  async changeItem(key, quantity) {
    if (!key || this.pendingItemKeys.has(key)) return;

    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      });

      if (!response.ok) {
        throw new Error('Unable to update cart item');
      }

      const cart = await response.json();
      this.render(cart);
    } catch (error) {
      console.error(error);
    } finally {
      this.pendingItemKeys.delete(key);
      this.setItemPendingState(key, false);
    }
  }

  async refresh() {
    if (this.isRefreshing) return null;

    this.isRefreshing = true;
    this.panel?.setAttribute('aria-busy', 'true');
    this.body?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: {
          Accept: 'application/json'
        }
      });
      const cart = await response.json();
      this.render(cart);
      return cart;
    } finally {
      this.isRefreshing = false;
      this.panel?.setAttribute('aria-busy', 'false');
      this.body?.setAttribute('aria-busy', 'false');
    }
  }

  render(cart) {
    this.hydrateCompareAtPrices(cart);
    const itemCount = cart.item_count || 0;

    if (this.drawerCount) {
      this.drawerCount.textContent = itemCount;
    }

    this.countNodes.forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });

    if (this.subtotalNode) {
      this.subtotalNode.textContent = this.formatMoney(cart.total_price || 0);
    }

    if (this.liveRegion) {
      this.liveRegion.textContent = this.liveRegionTemplate
        .replace('[[count]]', String(itemCount))
        .replace('[[subtotal]]', this.formatMoney(cart.total_price || 0));
    }

    const compareTotal = (cart.items || []).reduce(
      (sum, item) => sum + (item.compare_at_line_price || item.final_line_price || 0),
      0
    );
    if (this.compareSubtotalNode) {
      const showCompareSubtotal = this.showCompareAt && compareTotal > (cart.total_price || 0);
      this.compareSubtotalNode.hidden = !showCompareSubtotal;
      this.compareSubtotalNode.textContent = showCompareSubtotal ? this.formatMoney(compareTotal) : '';
    }

    if (this.footer) {
      this.footer.hidden = itemCount === 0;
    }

    this.renderUpgrade(cart);

    if (!itemCount) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty" data-cart-drawer-empty>
          <p class="cart-drawer__empty-title">${this.escapeHtml(this.emptyTitle)}</p>
          <p class="cart-drawer__empty-copy">${this.escapeHtml(this.emptyCopy)}</p>
        </div>
      `;
      return;
    }

    const itemsMarkup = cart.items
      .map((item) => {
        const imageMarkup = item.image
          ? `<a href="${item.url}" class="cart-drawer__item-media">
              <img src="${item.image}" alt="${this.escapeHtml(item.product_title)}" class="cart-drawer__item-image" loading="lazy">
            </a>`
          : `<div class="cart-drawer__item-media"></div>`;

        const variantMarkup =
          item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>`
            : '';

        const comparePriceMarkup =
          this.showCompareAt && (item.compare_at_line_price || 0) > (item.final_line_price || 0)
            ? `<p class="cart-drawer__item-compare-price">${this.formatMoney(item.compare_at_line_price)}</p>`
            : '';

        const isPending = this.pendingItemKeys.has(item.key);

        return `
          <li class="cart-drawer__item${isPending ? ' is-pending' : ''}" data-cart-item-key="${item.key}" aria-busy="${isPending ? 'true' : 'false'}">
            ${imageMarkup}
            <div class="cart-drawer__item-content">
              <div class="cart-drawer__item-top">
                <div>
                  <a href="${item.url}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
                  ${variantMarkup}
                </div>
                <div class="cart-drawer__item-prices">
                  <p class="cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</p>
                  ${comparePriceMarkup}
                </div>
              </div>
              <div class="cart-drawer__item-bottom">
                <div class="cart-drawer__quantity">
                  <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="${this.escapeHtml(this.decreaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>-</button>
                  <span class="cart-drawer__quantity-value">${item.quantity}</span>
                  <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="${this.escapeHtml(this.increaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>+</button>
                </div>
                <button type="button" class="cart-drawer__remove" data-cart-remove ${isPending ? 'disabled' : ''}>${this.escapeHtml(this.removeLabel)}</button>
              </div>
            </div>
          </li>
        `;
      })
      .join('');

    this.body.innerHTML = `<ul class="cart-drawer__items" data-cart-drawer-items role="list">${itemsMarkup}</ul>`;
  }

  renderUpgrade(cart) {
    const upgradeRoot = this.drawer.querySelector('[data-cart-drawer-upgrade]');
    if (!upgradeRoot || !this.upgradeConfig?.enabled) {
      this.stopCountdown();
      return;
    }

    const tiers = [...(this.upgradeConfig.tiers || [])]
      .filter((tier) => Number(tier.threshold) > 0 && tier.reward)
      .sort((a, b) => Number(a.threshold) - Number(b.threshold));

    const hasItems = (cart.item_count || 0) > 0;
    upgradeRoot.hidden = !hasItems || !tiers.length;

    if (!hasItems || !tiers.length) {
      this.resetCountdown();
      return;
    }

    const total = Number(cart.total_price || 0);
    const nextTier = tiers.find((tier) => total < Number(tier.threshold));
    const highestThreshold = Number(tiers[tiers.length - 1]?.threshold || 0);
    const progressNode = upgradeRoot.querySelector('[data-cart-drawer-upgrade-progress]');
    const copyNode = upgradeRoot.querySelector('[data-cart-drawer-upgrade-copy]');
    const tiersNode = upgradeRoot.querySelector('[data-cart-drawer-upgrade-tiers]');
    const legendNode = upgradeRoot.querySelector('[data-cart-drawer-upgrade-legend]');

    if (copyNode) {
      copyNode.textContent = nextTier
        ? this.upgradeRemainingTemplate
            .replace('[[amount]]', this.formatMoney(Number(nextTier.threshold) - total))
            .replace('[[reward]]', nextTier.reward)
        : this.upgradeConfig.completeText || this.upgradeCompleteLabel;
    }

    if (progressNode) {
      const progress = highestThreshold > 0 ? Math.min((total / highestThreshold) * 100, 100) : 0;
      progressNode.style.width = `${progress}%`;
    }

    if (tiersNode) {
      tiersNode.innerHTML = tiers
        .map((tier) => {
          const isComplete = total >= Number(tier.threshold);
          const position = highestThreshold > 0 ? Math.min((Number(tier.threshold) / highestThreshold) * 100, 100) : 0;
          return `
            <span
              class="cart-drawer__upgrade-marker${isComplete ? ' is-complete' : ''}"
              style="left: ${position}%;"
              title="${this.escapeHtml(`${tier.reward} - ${this.formatMoney(Number(tier.threshold))}`)}"
            >
              <span class="cart-drawer__upgrade-marker-icon" aria-hidden="true"><svg viewBox="0 0 10 10" focusable="false" role="presentation"><path d="M2 5.2 4.1 7.3 8 3.4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>
            </span>
          `;
        })
        .join('');
    }

    if (legendNode) {
      legendNode.innerHTML = tiers
        .map((tier) => {
          const isComplete = total >= Number(tier.threshold);
          return `
            <span class="cart-drawer__upgrade-pill${isComplete ? ' is-complete' : ''}">
              <span class="cart-drawer__upgrade-pill-dot" aria-hidden="true"></span>
              <span>${this.escapeHtml(tier.reward)}</span>
            </span>
          `;
        })
        .join('');
    }

    if (this.upgradeConfig.countdownEnabled) {
      this.startCountdown(hasItems);
    } else {
      this.stopCountdown();
      if (this.countdownNode) {
        this.countdownNode.hidden = true;
      }
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.panel
      .querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.lastActiveElement?.focus();
  }

  setAddButtonState(isLoading) {
    if (!this.activeAddButton) return;

    this.activeAddButton.disabled = isLoading;
    if (this.activeAddLabel) {
      this.activeAddLabel.textContent = isLoading ? this.addingLabel : this.activeAddLabelText;
    }
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;

    item.classList.toggle('is-pending', isPending);
    item.setAttribute('aria-busy', isPending ? 'true' : 'false');

    item.querySelectorAll('button').forEach((button) => {
      button.disabled = isPending;
    });
  }

  trapFocus(event) {
    const focusableNodes = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((node) => !node.hasAttribute('disabled'));
    if (!focusableNodes.length) return;

    const firstNode = focusableNodes[0];
    const lastNode = focusableNodes[focusableNodes.length - 1];

    if (event.shiftKey && document.activeElement === firstNode) {
      event.preventDefault();
      lastNode.focus();
    } else if (!event.shiftKey && document.activeElement === lastNode) {
      event.preventDefault();
      firstNode.focus();
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency
    }).format((cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  parseJsonNode(selector) {
    const node = this.drawer.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  parseVariantCompareAtPrices() {
    const value = this.parseJsonNode('[data-cart-drawer-compare-at-prices]');
    const prices = new Map();

    Object.entries(value || {}).forEach(([variantId, compareAtPrice]) => {
      const normalizedId = Number(variantId);
      if (!normalizedId) return;

      prices.set(normalizedId, Number(compareAtPrice || 0));
    });

    return prices;
  }

  cacheFormVariantCompareAt(form) {
    if (!this.showCompareAt) return;

    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId || this.variantCompareAtPrices.has(variantId)) return;

    const productJsonNode = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!productJsonNode) return;

    try {
      const product = JSON.parse(productJsonNode.textContent || '{}');
      const variant = (product.variants || []).find((item) => Number(item.id) === variantId);
      if (!variant) return;

      this.variantCompareAtPrices.set(variantId, Number(variant.compare_at_price || 0));
    } catch (error) {
      console.error(error);
    }
  }

  hydrateCompareAtPrices(cart) {
    if (!this.showCompareAt || !cart?.items?.length) return;

    cart.items.forEach((item) => {
      const variantId = Number(item.variant_id || 0);
      const compareAtUnitPrice = Number(this.variantCompareAtPrices.get(variantId) || 0);
      const finalUnitPrice = Number(item.final_price || 0);

      item.compare_at_unit_price = compareAtUnitPrice;
      item.compare_at_line_price =
        compareAtUnitPrice > finalUnitPrice ? compareAtUnitPrice * Number(item.quantity || 0) : 0;
    });
  }

  startCountdown(hasItems) {
    if (!this.countdownNode || !this.countdownTimeNode) return;

    this.countdownNode.hidden = !hasItems;
    if (!hasItems) {
      this.resetCountdown();
      return;
    }

    let endsAt = Number(this.getStoredCountdownEnd() || 0);
    if (!endsAt || endsAt <= Date.now()) {
      const minutes = Number(this.upgradeConfig?.countdownMinutes || 10);
      endsAt = Date.now() + (minutes * 60 * 1000);
      this.setStoredCountdownEnd(endsAt);
    }

    this.updateCountdown(endsAt);
    this.stopCountdown();
    this.countdownInterval = window.setInterval(() => this.updateCountdown(endsAt), 1000);
  }

  updateCountdown(endsAt) {
    if (!this.countdownTimeNode) return;

    const remaining = Math.max(0, endsAt - Date.now());
    const totalSeconds = Math.ceil(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    this.countdownTimeNode.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    if (remaining <= 0) {
      this.stopCountdown();
    }
  }

  stopCountdown() {
    if (this.countdownInterval) {
      window.clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  resetCountdown() {
    this.stopCountdown();
    this.clearStoredCountdownEnd();
    if (this.countdownNode) {
      this.countdownNode.hidden = true;
    }
  }

  getStoredCountdownEnd() {
    try {
      return window.localStorage.getItem(this.countdownStorageKey);
    } catch (error) {
      return null;
    }
  }

  setStoredCountdownEnd(value) {
    try {
      window.localStorage.setItem(this.countdownStorageKey, String(value));
    } catch (error) {
      console.error(error);
    }
  }

  clearStoredCountdownEnd() {
    try {
      window.localStorage.removeItem(this.countdownStorageKey);
    } catch (error) {
      console.error(error);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CartDrawerController();
});
